import { useForm } from "react-hook-form";

import { useErrors } from "@auth0/auth0-acul-react/mfa-otp-challenge";

import {
  ULThemeFloatingLabelField,
  ULThemeFormMessage,
} from "@/components/form";
import RequiredFooter from "@/components/RequiredFooter";
import { Form, FormField, FormItem } from "@/components/ui/form";
import { ULThemeButton } from "@/components/ULThemeButton";
import { ULThemeAlert, ULThemeAlertTitle } from "@/components/ULThemeError";

import { useMfaOtpChallengeManager } from "../hooks/useMfaOtpChallengeManager";

// No `rememberDevice` field. See the manager hook: Auth0 offers a
// "Remember this device for 30 days" option on this screen and it is
// deliberately suppressed, because a remembered browser can satisfy MFA on a
// later step-up WITHOUT a fresh factor challenge — which is precisely what
// this gate exists to prevent. Do not add it back.
interface OtpFormValues {
  code: string;
}

function MfaOtpChallengeForm() {
  const { handleSubmitOtp, texts, locales } = useMfaOtpChallengeManager();

  const { errors, dismiss } = useErrors();

  const form = useForm<OtpFormValues>({
    defaultValues: {
      code: "",
    },
  });

  const {
    formState: { isSubmitting },
  } = form;

  const buttonText = texts?.buttonText || locales?.form?.submitButton;
  const codeLabelText = texts?.placeholder || locales?.form?.codeLabel;

  const generalErrors = errors.byType("auth0").filter((err) => !err.field);
  const codeSDKError = errors.byField("code")[0]?.message;

  const onSubmit = async (formData: OtpFormValues) => {
    await handleSubmitOtp(formData.code);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        {generalErrors.length > 0 && (
          <div className="space-y-3 mb-4">
            {generalErrors.map((error) => (
              <ULThemeAlert
                key={error.id}
                variant="destructive"
                onDismiss={() => dismiss(error.id)}
              >
                <ULThemeAlertTitle>{error.message}</ULThemeAlertTitle>
              </ULThemeAlert>
            ))}
          </div>
        )}

        {/* TOTP code.
         *
         * autoComplete="one-time-code" lets the platform offer the code from
         * an authenticator that supports it, and inputMode="numeric" brings up
         * the digit keypad on mobile — the difference between a six-tap entry
         * and hunting for the number row while a 30-second window runs down.
         *
         * maxLength is NOT set. A member pasting a code with a stray space is
         * better served by the trim() in the manager than by the field
         * silently swallowing their sixth digit. */}
        <FormField
          control={form.control}
          name="code"
          rules={{
            required: locales?.form?.codeRequired,
          }}
          render={({ field, fieldState }) => (
            <FormItem>
              <ULThemeFloatingLabelField
                {...field}
                label={codeLabelText}
                type="text"
                inputMode="numeric"
                placeholder=""
                autoComplete="one-time-code"
                autoFocus
                error={!!fieldState.error || !!codeSDKError}
              />
              <ULThemeFormMessage
                sdkError={codeSDKError}
                hasFormError={!!fieldState.error}
              />
            </FormItem>
          )}
        />

        <ULThemeButton
          type="submit"
          variant="primary"
          className="w-full"
          disabled={isSubmitting}
        >
          {isSubmitting ? locales?.form?.submitting : buttonText}
        </ULThemeButton>
        <RequiredFooter />
      </form>
    </Form>
  );
}

export default MfaOtpChallengeForm;
